import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { CanvasHost } from '../hooks/use-storefront-canvas-host';
import { useWebsiteT } from '../hooks/use-website-t';
import { computeFrameViewport, type FrameDevice } from '../lib/frame-viewport';
import { FrameStatusStates } from './frame-status-states';

/** C11 disable contract 5: scripts and forms, never top navigation, no geolocation. */
export const CANVAS_IFRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox';

export interface StorefrontFrameProps {
  readonly host: CanvasHost;
  readonly device: FrameDevice;
  /** Accessible name of the frame. */
  readonly title: string;
  readonly className?: string;
}

function useElementWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    setWidth(element.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setWidth(next);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/**
 * The real storefront, in an iframe of the storefront origin. The element is
 * never re-keyed by a device switch or by edit mode: only its wrapper changes
 * width, so the route, the scroll position and the draft cart stay. It scrolls
 * on its own, independently of the Admin page.
 */
export function StorefrontFrame({
  host,
  device,
  title,
  className,
}: StorefrontFrameProps): React.JSX.Element {
  const { dir } = useWebsiteT();
  const [containerRef, available] = useElementWidth();
  const viewport = computeFrameViewport(device, available);

  return (
    <div
      ref={containerRef}
      data-testid="storefront-frame-viewport"
      data-device={device}
      data-canvas-instance={host.canvasInstanceId ?? undefined}
      className={cn(
        'bg-admin-surface-muted relative min-h-0 min-w-0 flex-1 overflow-auto',
        className,
      )}
    >
      <FrameStatusStates status={host.status} onRetry={host.refresh} />
      <div className="mx-auto h-full" style={{ width: viewport.outerWidth }}>
        <div
          style={{
            width: viewport.width,
            height: viewport.scale === 1 ? '100%' : `${100 / viewport.scale}%`,
            transform: viewport.scale === 1 ? undefined : `scale(${viewport.scale})`,
            transformOrigin: dir === 'rtl' ? 'top right' : 'top left',
          }}
        >
          {host.frameSrc === null ? null : (
            <iframe
              key={host.frameKey}
              ref={host.iframeRef}
              title={title}
              src={host.frameSrc}
              sandbox={CANVAS_IFRAME_SANDBOX}
              onLoad={host.onFrameLoad}
              data-testid="storefront-frame"
              className={cn(
                'h-full w-full border-0 bg-white',
                device === 'mobile' && 'border-border shadow-admin-elevated border',
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
